import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Brand } from "../components/Brand";
import { supabase } from "../lib/supabase";

interface LoginSearch {
  redirect?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const redirect = search.redirect;
    return typeof redirect === "string" ? { redirect } : {};
  },
  beforeLoad: async ({ context, search }) => {
    // Already signed in? Skip the form and go straight to the target.
    const { data } = await context.supabase.auth.getSession();
    if (data.session) {
      const target =
        typeof search.redirect === "string" && search.redirect.startsWith("/")
          ? search.redirect
          : "/projects";
      // Using window.location so the redirect target is treated as an
      // arbitrary internal path rather than a typed route.
      window.location.replace(target);
    }
  },
  component: Login,
});

function Login() {
  const navigate = useNavigate();
  const { redirect: redirectParam } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setSubmitting(false);

    if (signInError) {
      setError(signInError.message);
      return;
    }

    const target =
      redirectParam && redirectParam.startsWith("/") ? redirectParam : "/projects";
    if (target === "/projects") {
      await navigate({ to: "/projects" });
    } else {
      window.location.replace(target);
    }
  }

  return (
    <main className="auth-shell">
      <div className="auth-card">
        <Brand size="lg" />
        <h1>Sign in</h1>
        <form onSubmit={handleSubmit}>
          <label className="form-field">
            <span>Email</span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {error ? (
            <p className="alert" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}
